import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiUrl, brands } from "../config/config";
import StripeCheckout from "./StripeCheckout";
import SEO from "./SEO";

const getUserOrGuestId = () => {
  const user = localStorage.getItem("user");

  if (user) {
    try {
      const parsed = JSON.parse(user);
      if (parsed?.id) return { user_id: parsed.id, guest_id: null };
    } catch {}
  }

  let guest_id = localStorage.getItem("guest_id");
  if (!guest_id) {
    guest_id = "guest_" + Math.random().toString(36).substring(2, 12);
    localStorage.setItem("guest_id", guest_id);
  }

  return { user_id: null, guest_id };
};

const calculateDeliveryDate = (daysToAdd) => {
  const date = new Date();
  date.setDate(date.getDate() + daysToAdd);
  return date.toISOString().split("T")[0];
};

const SkeletonBox = ({ className = "" }) => {
  return (
    <div
      className={`animate-pulse rounded-[14px] bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%] ${className}`}
    />
  );
};

const CheckoutSkeleton = () => {
  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <div className="mx-auto w-full max-w-[1500px] px-4 py-8 sm:px-6 sm:py-10 lg:px-10 lg:py-12">
        <div className="mb-7 rounded-[24px] border border-[#e5eaf0] bg-white p-5 shadow-[0_12px_35px_rgba(15,35,54,0.08)] sm:p-7">
          <SkeletonBox className="h-10 w-[220px] sm:h-12 sm:w-[300px]" />
          <SkeletonBox className="mt-4 h-5 w-full max-w-[520px]" />
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_480px] xl:grid-cols-[1fr_520px]">
          <div className="space-y-5">
            <div className="rounded-[24px] border border-[#e5eaf0] bg-white p-5 shadow-[0_12px_35px_rgba(15,35,54,0.08)] sm:p-7">
              <SkeletonBox className="mb-6 h-7 w-[190px]" />

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <SkeletonBox className="h-[74px] w-full" />
                <SkeletonBox className="h-[74px] w-full" />
                <SkeletonBox className="h-[74px] w-full md:col-span-2" />
                <SkeletonBox className="h-[74px] w-full" />
                <SkeletonBox className="h-[74px] w-full" />
                <SkeletonBox className="h-[74px] w-full" />
                <SkeletonBox className="h-[74px] w-full" />
                <SkeletonBox className="h-[74px] w-full md:col-span-2" />
              </div>
            </div>

            <div className="rounded-[24px] border border-[#e5eaf0] bg-white p-5 shadow-[0_12px_35px_rgba(15,35,54,0.08)] sm:p-7">
              <SkeletonBox className="h-6 w-[280px]" />
              <SkeletonBox className="mt-5 h-[120px] w-full" />
            </div>
          </div>

          <div className="rounded-[24px] border border-[#e5eaf0] bg-white p-5 shadow-[0_12px_35px_rgba(15,35,54,0.08)] sm:p-6">
            <SkeletonBox className="h-7 w-[180px]" />
            <SkeletonBox className="mt-5 h-px w-full" />

            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className="mt-5 flex items-center gap-4 border-b border-[#e5eaf0] pb-5"
              >
                <SkeletonBox className="h-16 w-16 shrink-0 rounded-[14px]" />
                <div className="w-full">
                  <SkeletonBox className="h-4 w-3/4" />
                  <SkeletonBox className="mt-3 h-4 w-1/2" />
                </div>
                <SkeletonBox className="h-5 w-[70px]" />
              </div>
            ))}

            <div className="mt-6 space-y-4">
              <SkeletonBox className="h-5 w-full" />
              <SkeletonBox className="h-5 w-full" />
              <SkeletonBox className="h-5 w-full" />
              <SkeletonBox className="h-12 w-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Checkout = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [countries, setCountries] = useState([]);
  const [errors, setErrors] = useState({});
  const [zip, setZip] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");

  const [isShippingSame, setIsShippingSame] = useState(true);
  const [shippingFirstName, setShippingFirstName] = useState("");
  const [shippingLastName, setShippingLastName] = useState("");
  const [shippingAddress, setShippingAddress] = useState("");
  const [shippingCity, setShippingCity] = useState("");
  const [shippingCountry, setShippingCountry] = useState("");
  const [shippingZip, setShippingZip] = useState("");
  const [shippingErrors, setShippingErrors] = useState({});

  const [isRushDelivery, setIsRushDelivery] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState(calculateDeliveryDate(14));
  const [shippingCharge, setShippingCharge] = useState(20);

  const navigate = useNavigate();
  const subtotal = cartItems.reduce((sum, item) => sum + Number(item.price), 0);
  const total = subtotal + shippingCharge;

  useEffect(() => {
    fetch("https://backend.inkox.com/api/countries")
      .then((res) => res.json())
      .then((data) => setCountries(data))
      .catch((err) => console.error("Country fetch error:", err));
  }, []);

  useEffect(() => {
    if (isRushDelivery) {
      setDeliveryDate(calculateDeliveryDate(7));
      setShippingCharge(20 + subtotal * 0.2);
    } else {
      setDeliveryDate(calculateDeliveryDate(14));
      setShippingCharge(20);
    }
  }, [isRushDelivery, subtotal]);

  useEffect(() => {
    if (isShippingSame) {
      setShippingFirstName(firstName);
      setShippingLastName(lastName);
      setShippingAddress(address);
      setShippingCity(city);
      setShippingCountry(country);
      setShippingZip(zip);
    }
  }, [isShippingSame, firstName, lastName, address, city, country, zip]);

  useEffect(() => {
    const { user_id, guest_id } = getUserOrGuestId();

    const url = user_id
      ? `${apiUrl}/api/cart/user/${user_id}`
      : `${apiUrl}/api/cart?guest_id=${guest_id}`;

    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setCartItems(data.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log("Checkout fetch error:", err);
        setLoading(false);
      });
  }, []);

  const inputClass = (hasError) =>
    `w-full mt-2 px-4 py-3 rounded-xl border bg-white text-[#2b2b2b] font-medium outline-none transition-all duration-300 ${
      hasError
        ? "border-red-500 focus:ring-4 focus:ring-red-100"
        : "border-[#dbe7f3] focus:border-[#f97316]/60 focus:ring-4 focus:ring-[#f97316]/10"
    }`;

  const validateBillingForm = () => {
    const newErrors = {};

    if (!firstName) newErrors.firstName = true;
    if (!lastName) newErrors.lastName = true;
    if (!address) newErrors.address = true;
    if (!city) newErrors.city = true;
    if (!country) newErrors.country = true;
    if (!zip) newErrors.zip = true;
    if (!mobile) newErrors.mobile = true;
    if (!email) newErrors.email = true;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateShippingForm = () => {
    if (isShippingSame) return true;

    const newErrors = {};

    if (!shippingFirstName) newErrors.shippingFirstName = true;
    if (!shippingLastName) newErrors.shippingLastName = true;
    if (!shippingAddress) newErrors.shippingAddress = true;
    if (!shippingCity) newErrors.shippingCity = true;
    if (!shippingCountry) newErrors.shippingCountry = true;
    if (!shippingZip) newErrors.shippingZip = true;

    setShippingErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateAllForms = () => {
    const isBillingValid = validateBillingForm();
    const isShippingValid = validateShippingForm();
    return isBillingValid && isShippingValid;
  };

  const placeOrder = async (payment_id, paymentStatus = "paid") => {
    const { user_id, guest_id } = getUserOrGuestId();

    const shippingAddressObj = {
      first_name: shippingFirstName,
      last_name: shippingLastName,
      address: shippingAddress,
      city: shippingCity,
      country: shippingCountry,
      zip: shippingZip,
      is_same_as_billing: isShippingSame,
    };

    const payload = {
      user_id,
      guest_id,
      first_name: firstName,
      last_name: lastName,
      address,
      city,
      country,
      zip,
      mobile,
      email,
      notes,
      subtotal,
      shipping: shippingCharge,
      total,
      payment_method: "stripe",
      payment_id,
      payment_status: paymentStatus,
      brand: brands,
      cart_items: cartItems,
      shipping_address: shippingAddressObj,
      delivery_date: {
        date: deliveryDate,
        is_rush_delivery: isRushDelivery,
      },
    };

    const res = await fetch(`${apiUrl}/api/checkout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await res.json();

    if (data.success) {
      navigate("/thank-you", { state: { orderId: data.order_id } });
    }
  };

  if (loading) {
    return <CheckoutSkeleton />;
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-[#f8fafc] px-4 py-10 text-center sm:py-14">
        <div className="mx-auto max-w-[720px] rounded-[24px] border border-[#e5eaf0] bg-white px-5 py-10 shadow-[0_12px_35px_rgba(15,35,54,0.08)]">
          <h2 className="text-3xl font-bold text-[#2b2b2b]">
            Your cart is empty
          </h2>

          <a
            href="/products"
            className="mt-6 inline-flex rounded-[14px] bg-[#0a2336] px-7 py-3 text-[15px] font-extrabold uppercase tracking-[0.5px] text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#f97316]"
          >
            Continue Shopping
          </a>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SEO
        title="Checkout"
        description="Patch Designers For Hats"
        keywords="Patch Designers For Hats"
      />

      <div className="min-h-screen bg-[#f8fafc]">
        <div className="mx-auto w-full max-w-[1500px] px-4 py-8 sm:px-6 sm:py-10 lg:px-10 lg:py-12">
          <div className="mb-7 rounded-[24px] border border-[#e5eaf0] bg-white p-5 shadow-[0_12px_35px_rgba(15,35,54,0.08)] sm:p-7">
         
            <h2 className="text-[30px] font-extrabold leading-tight tracking-wide text-[#2b2b2b] sm:text-[40px] lg:text-[48px]">
              Checkout
            </h2>

            <p className="mt-3 max-w-[760px] text-[15px] font-medium leading-[26px] text-[#415164] sm:text-[16px]">
              Complete your billing and shipping details to place your custom
              patch order.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_480px] xl:grid-cols-[1fr_520px]">
            <div className="space-y-5">
              <div className="rounded-[24px] border border-[#e5eaf0] bg-white p-5 shadow-[0_12px_35px_rgba(15,35,54,0.08)] sm:p-7">
                <h3 className="mb-5 text-[22px] font-extrabold text-[#2b2b2b] sm:text-[24px]">
                  Billing Details
                </h3>

                <form className="space-y-5">
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div>
                      <label className="text-sm font-bold text-[#0a2336]">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        className={inputClass(errors.firstName)}
                        placeholder="Enter your first name"
                        value={firstName}
                        onChange={(e) => {
                          setFirstName(e.target.value);
                          setErrors({ ...errors, firstName: false });
                        }}
                      />
                      {errors.firstName && (
                        <p className="mt-1 text-sm text-red-500">
                          First name is required
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm font-bold text-[#0a2336]">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        className={inputClass(errors.lastName)}
                        placeholder="Enter your last name"
                        value={lastName}
                        onChange={(e) => {
                          setLastName(e.target.value);
                          setErrors({ ...errors, lastName: false });
                        }}
                      />
                      {errors.lastName && (
                        <p className="mt-1 text-sm text-red-500">
                          Last name is required
                        </p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="text-sm font-bold text-[#0a2336]">
                        Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        className={inputClass(errors.address)}
                        placeholder="Enter your address"
                        value={address}
                        onChange={(e) => {
                          setAddress(e.target.value);
                          setErrors({ ...errors, address: false });
                        }}
                      />
                      {errors.address && (
                        <p className="mt-1 text-sm text-red-500">
                          Address is required
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm font-bold text-[#0a2336]">
                        Town / City <span className="text-red-500">*</span>
                      </label>
                      <input
                        className={inputClass(errors.city)}
                        placeholder="Enter your city"
                        value={city}
                        onChange={(e) => {
                          setCity(e.target.value);
                          setErrors({ ...errors, city: false });
                        }}
                      />
                      {errors.city && (
                        <p className="mt-1 text-sm text-red-500">
                          City is required
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm font-bold text-[#0a2336]">
                        Country <span className="text-red-500">*</span>
                      </label>
                      <select
                        className={inputClass(errors.country)}
                        value={country}
                        onChange={(e) => {
                          setCountry(e.target.value);
                          setErrors({ ...errors, country: false });
                        }}
                      >
                        <option value="">Select a country</option>
                        {countries.map((c) => (
                          <option key={c.code} value={c.name}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                      {errors.country && (
                        <p className="mt-1 text-sm text-red-500">
                          Country is required
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm font-bold text-[#0a2336]">
                        Postcode / ZIP <span className="text-red-500">*</span>
                      </label>
                      <input
                        className={inputClass(errors.zip)}
                        placeholder="Enter ZIP"
                        value={zip}
                        onChange={(e) => {
                          setZip(e.target.value);
                          setErrors({ ...errors, zip: false });
                        }}
                      />
                      {errors.zip && (
                        <p className="mt-1 text-sm text-red-500">
                          ZIP is required
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="text-sm font-bold text-[#0a2336]">
                        Mobile <span className="text-red-500">*</span>
                      </label>
                      <input
                        className={inputClass(errors.mobile)}
                        placeholder="Enter mobile number"
                        value={mobile}
                        onChange={(e) => {
                          setMobile(e.target.value);
                          setErrors({ ...errors, mobile: false });
                        }}
                      />
                      {errors.mobile && (
                        <p className="mt-1 text-sm text-red-500">
                          Mobile number is required
                        </p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="text-sm font-bold text-[#0a2336]">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        className={inputClass(errors.email)}
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          setErrors({ ...errors, email: false });
                        }}
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-500">
                          Email is required
                        </p>
                      )}
                    </div>
                  </div>
                </form>
              </div>

              <div className="rounded-[24px] border border-[#e5eaf0] bg-white p-5 shadow-[0_12px_35px_rgba(15,35,54,0.08)] sm:p-7">
                <label className="flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    checked={isShippingSame}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsShippingSame(checked);

                      if (checked) {
                        setShippingFirstName(firstName || "");
                        setShippingLastName(lastName || "");
                        setShippingAddress(address || "");
                        setShippingCity(city || "");
                        setShippingCountry(country || "");
                        setShippingZip(zip || "");
                        setShippingErrors({});
                      } else {
                        setShippingFirstName("");
                        setShippingLastName("");
                        setShippingAddress("");
                        setShippingCity("");
                        setShippingCountry("");
                        setShippingZip("");
                      }
                    }}
                    className="h-5 w-5 rounded border-gray-300 text-[#f97316] focus:ring-[#f97316]"
                  />
                  <span className="ml-3 font-bold text-[#0a2336]">
                    Shipping address same as billing address
                  </span>
                </label>

                {!isShippingSame && (
                  <div className="mt-6">
                    <h3 className="mb-5 text-[22px] font-extrabold text-[#2b2b2b] sm:text-[24px]">
                      Shipping Details
                    </h3>

                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                      <div>
                        <label className="text-sm font-bold text-[#0a2336]">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          className={inputClass(
                            shippingErrors.shippingFirstName
                          )}
                          placeholder="Enter shipping first name"
                          value={shippingFirstName}
                          onChange={(e) => {
                            setShippingFirstName(e.target.value);
                            setShippingErrors({
                              ...shippingErrors,
                              shippingFirstName: false,
                            });
                          }}
                        />
                      </div>

                      <div>
                        <label className="text-sm font-bold text-[#0a2336]">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          className={inputClass(shippingErrors.shippingLastName)}
                          placeholder="Enter shipping last name"
                          value={shippingLastName}
                          onChange={(e) => {
                            setShippingLastName(e.target.value);
                            setShippingErrors({
                              ...shippingErrors,
                              shippingLastName: false,
                            });
                          }}
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="text-sm font-bold text-[#0a2336]">
                          Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          className={inputClass(shippingErrors.shippingAddress)}
                          placeholder="Enter shipping address"
                          value={shippingAddress}
                          onChange={(e) => {
                            setShippingAddress(e.target.value);
                            setShippingErrors({
                              ...shippingErrors,
                              shippingAddress: false,
                            });
                          }}
                        />
                      </div>

                      <div>
                        <label className="text-sm font-bold text-[#0a2336]">
                          Town / City <span className="text-red-500">*</span>
                        </label>
                        <input
                          className={inputClass(shippingErrors.shippingCity)}
                          placeholder="Enter shipping city"
                          value={shippingCity}
                          onChange={(e) => {
                            setShippingCity(e.target.value);
                            setShippingErrors({
                              ...shippingErrors,
                              shippingCity: false,
                            });
                          }}
                        />
                      </div>

                      <div>
                        <label className="text-sm font-bold text-[#0a2336]">
                          Country <span className="text-red-500">*</span>
                        </label>
                        <select
                          className={inputClass(shippingErrors.shippingCountry)}
                          value={shippingCountry}
                          onChange={(e) => {
                            setShippingCountry(e.target.value);
                            setShippingErrors({
                              ...shippingErrors,
                              shippingCountry: false,
                            });
                          }}
                        >
                          <option value="">Select a country</option>
                          {countries.map((c) => (
                            <option key={c.code} value={c.name}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="text-sm font-bold text-[#0a2336]">
                          Postcode / ZIP <span className="text-red-500">*</span>
                        </label>
                        <input
                          className={inputClass(shippingErrors.shippingZip)}
                          placeholder="Enter shipping ZIP"
                          value={shippingZip}
                          onChange={(e) => {
                            setShippingZip(e.target.value);
                            setShippingErrors({
                              ...shippingErrors,
                              shippingZip: false,
                            });
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <textarea
                className="w-full rounded-[24px] border border-[#e5eaf0] bg-white px-5 py-4 text-[15px] font-medium text-[#2b2b2b] shadow-[0_12px_35px_rgba(15,35,54,0.08)] outline-none transition-all duration-300 placeholder:text-gray-400 focus:border-[#f97316]/60 focus:ring-4 focus:ring-[#f97316]/10"
                rows="5"
                placeholder="Order notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div className="h-fit overflow-hidden rounded-[24px] border border-[#e5eaf0] bg-white shadow-[0_12px_35px_rgba(15,35,54,0.08)] lg:sticky lg:top-6">
              <div className="border-b border-[#e5eaf0] bg-white px-6 py-5">
                <h3 className="text-[22px] font-extrabold text-[#2b2b2b] sm:text-[24px]">
                  Order Summary
                </h3>
              </div>

              <div className="divide-y divide-[#e5eaf0]">
                {Array.from(
                  new Map(
                    cartItems.map((item) => [item.product_name, item])
                  ).values()
                ).map((item) => {
                  const totalPrice = Number(item.price);
                  const unitPrice = item.qty ? totalPrice / item.qty : 0;

                  return (
                    <div key={item.id} className="flex gap-4 px-6 py-5">
                      <img
                        src={
                          item.upload_image
                            ? `${apiUrl}/storage/${item.upload_image}`
                            : "../img/no-image.png"
                        }
                        className="h-16 w-16 shrink-0 rounded-[14px] border border-[#e5eaf0] bg-[#f8fafc] object-contain"
                        alt={item.product_name}
                      />

                      <div className="min-w-0 flex-1">
                        <h4 className="text-[15px] font-extrabold leading-[22px] text-[#0a2336]">
                          {item.product_name}
                        </h4>

                        <p className="mt-1 text-[13px] font-medium text-[#415164]">
                          ${unitPrice.toFixed(2)} × {item.qty}
                        </p>
                      </div>

                      <p className="text-[15px] font-extrabold text-[#2b2b2b]">
                        ${totalPrice.toFixed(2)}
                      </p>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-[#e5eaf0] bg-[#fff7ed] px-6 py-5">
                <label className="flex cursor-pointer items-center justify-between gap-4">
                  <span className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isRushDelivery}
                      onChange={(e) => setIsRushDelivery(e.target.checked)}
                      className="h-5 w-5 rounded border-gray-300 text-[#f97316] focus:ring-[#f97316]"
                    />
                    <span className="ml-3 font-bold text-[#0a2336]">
                      Rush Delivery (7 days)
                    </span>
                  </span>

                  {isRushDelivery && (
                    <span className="text-sm font-bold text-[#f97316]">
                      +20% Rush Fee
                    </span>
                  )}
                </label>
              </div>

              <div className="space-y-4 px-6 py-5">
                <div className="flex justify-between text-[15px] font-bold text-[#415164]">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>

                <div className="flex justify-between text-[15px] font-bold text-[#415164]">
                  <span>
                    Shipping{" "}
                    {isRushDelivery && (
                      <span className="text-sm text-[#f97316]">
                        (Rush Delivery)
                      </span>
                    )}
                  </span>
                  <span>${shippingCharge.toFixed(2)}</span>
                </div>

                <div className="rounded-[14px] bg-[#fff7ed] px-4 py-3 text-[14px] font-bold text-[#0a2336]">
                  Expected Delivery Date:{" "}
                  <strong className="text-[#f97316]">{deliveryDate}</strong>
                </div>

                <div className="flex justify-between border-t border-[#e5eaf0] pt-5 text-[20px] font-extrabold text-[#2b2b2b]">
                  <span>Total</span>
                  <span>${total.toFixed(2)}</span>
                </div>

                <div className="pt-2">
                  <StripeCheckout
                    orderData={{
                      first_name: firstName,
                      last_name: lastName,
                      email,
                      total,
                    }}
                    validateForm={validateAllForms}
                    onSuccess={async (paymentId) => {
                      await placeOrder(paymentId, "paid");
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;